import { Typography, ListItem, Box, Button } from "@mui/material";
import { Task } from "protogen/tasks_pb";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "../hooks/useIsMobile";
import { PriorityChip } from "../creation/PrioritySelect";
import { dateStringToDayAndDate } from "./utils";
import { StageChip } from "./workflowStates";
import WithDividers from "components/helpers/WithDividers";
import { ProjectLabel } from "./TaskRow";
import { useNavigate } from "react-router-dom";
import { useMemberDetails } from "components/context/MemberDetailsContext";

export default ({
  tasks,
  sx,
  minimal = false,
  showAddTasksButton = false,
  loading = false,
  compact = false,
}: {
  tasks: Task[];
  sx?: React.CSSProperties;
  minimal?: boolean;
  showAddTasksButton?: boolean;
  loading?: boolean;
  compact?: boolean;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data: memberDetails } = useMemberDetails();
  const onClick = () => {
    const family = memberDetails?.family;
    if (family) {
      navigate("/tasks/add", { state: { family } });
    }
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {!loading && tasks.length === 0 && (
        <ListItem
          disablePadding
          sx={{
            maxWidth: "900px",
          }}
        >
          <Typography
            variant="body"
            color="text.tertiary"
            sx={{
              marginBottom: isMobile ? "8px" : "4px",
            }}
          >
            There are no open tasks.
          </Typography>
        </ListItem>
      )}
      {showAddTasksButton && (
        <Box>
          <Button onClick={onClick} variant="outlined">
            Add a task
          </Button>
        </Box>
      )}
      {tasks.map((task, index) => {
        return (
          <LinkRouter
            sx={{ padding: "0px" }}
            key={index}
            to={`/tasks/${encodeURIComponent(task.ref)}`}
          >
            <Box
              sx={(theme) => ({
                width: "100%",
                border: `1px solid ${theme.palette.gray[4]}`,
                borderRadius: "12px",
                padding: compact ? "12px 16px" : "16px 20px",
                ...sx,
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: compact ? "column" : "row",
                  alignItems: compact ? "start" : "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: compact ? "column" : "row",
                    alignItems: compact ? "start" : "center",
                    gap: "4px",
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body"
                    color="text.primary"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {compact && (
                      <StageChip
                        stage={task.stage}
                        accountType="member"
                        stageDate={minimal ? undefined : task.stageDate}
                        sx={{ flexShrink: 0 }}
                      />
                    )}
                    {!minimal && compact && (
                      <PriorityChip
                        priority={task.priority}
                        isMobile={isMobile}
                      />
                    )}
                    <WithDividers DividerComponent={() => <span>{" • "}</span>}>
                      {[
                        task.project ? (
                          <ProjectLabel project={task.project} key={0} />
                        ) : null,
                        task.dueDate ? (
                          <Typography
                            key={1}
                            variant="bodySmall"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >{`Due ${
                            dateStringToDayAndDate(task.dueDate, false)[1]
                          }`}</Typography>
                        ) : null,
                      ].filter(Boolean)}
                    </WithDividers>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    width: isMobile ? "100%" : "fit-content",
                    alignItems: "center",
                    flexDirection: isMobile ? "row" : "row-reverse",
                    gap: isMobile ? "8px" : "12px",
                  }}
                >
                  {!minimal && !compact && task.priority !== "medium" && (
                    <PriorityChip
                      priority={task.priority}
                      isMobile={isMobile}
                    />
                  )}
                  {!compact && (
                    <StageChip
                      stage={task.stage}
                      accountType="member"
                      stageDate={minimal ? undefined : task.stageDate}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </LinkRouter>
        );
      })}
    </Box>
  );
};
